import styled from '@emotion/styled';
import { css, keyframes } from '@emotion/core';
import { Typography } from '@kizen/kds/Typography';
import { codeHighlightTokensCss } from 'app/codeHighlight';
import { monoFontFamily } from 'app/typography';

export const MONO_FONT = monoFontFamily;

export const PanelRoot = styled.div`
  height: 100%;
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--color-background-white);
  color: var(--color-font-primary);
`;

export const PanelHeader = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-width: 0;
  height: 50px;
  padding: 0 15px;
  border-bottom: 1px solid var(--color-border-medium-light);
`;

export const HeaderTitle = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

export const HeaderName = styled(Typography)`
  min-width: 0;
`;

export const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

export const HeaderDivider = styled.span`
  width: 1px;
  height: 16px;
  background: var(--color-border-medium-light);
`;

export const StatusText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  white-space: nowrap;
`;

export const PanelSection = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 15px;
  border-bottom: 1px solid var(--color-border-medium-light);
  min-width: 0;
`;

export const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
`;

export const ChatFrame = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

export const ChatScroll = styled.div`
  flex: 1;
  min-height: 0;
  min-width: 0;
  overflow-x: hidden;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  overflow-anchor: none;
`;

export const ChatList = styled.div<{ $empty: boolean }>`
  flex-grow: ${({ $empty }) => ($empty ? 1 : 0)};
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 15px;
  min-width: 0;
`;

export const EmptyChat = styled.div`
  margin: auto 0;
  padding: 30px 15px;
  text-align: center;
`;

const skeletonPulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
`;

const skeletonReveal = ({ $revealed }: { $revealed: boolean }) => css`
  opacity: ${$revealed ? 1 : 0};
  transition: opacity 150ms ease-out;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const SkeletonRoot = styled.div<{ $revealed: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  ${skeletonReveal};
`;

export const SkeletonInline = styled.span<{ $revealed: boolean }>`
  display: inline-flex;
  ${skeletonReveal};
`;

export const SkeletonLines = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

export const SkeletonBar = styled.div<{ $width: string; $height: number }>`
  flex-shrink: 0;
  width: ${({ $width }) => $width};
  max-width: 100%;
  height: ${({ $height }) => $height}px;
  border-radius: 4px;
  background: var(--color-background-light);
  animation: ${skeletonPulse} 1.6s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const SkeletonUserBubble = styled(SkeletonBar)`
  align-self: flex-end;
  border-radius: 8px 8px 0 8px;
`;

export const SkeletonBarInline = SkeletonBar.withComponent('span');

export const SkeletonToolRow = styled.div`
  display: flex;
  align-items: center;
  height: 28px;
  padding: 0 8px;
  border: 1px solid var(--color-border-medium-light);
  border-radius: 4px;
`;

export const UserBubble = styled.div`
  align-self: flex-end;
  max-width: 88%;
  padding: 8px 12px;
  border-radius: 8px 8px 0 8px;
  background: var(--color-background-light);
  color: var(--color-font-primary);
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

export const AssistantText = styled.div`
  min-width: 0;
  overflow-wrap: anywhere;

  & .kds pre {
    margin: 8px 0;
    padding: 8px 10px;
    border: 1px solid var(--color-border-medium-light);
    border-radius: 4px;
    background: var(--color-background-lightest);
    overflow-x: auto;
    font-family: ${MONO_FONT};
    font-size: 11px;
    line-height: 16px;
    white-space: pre;
    tab-size: 2;
  }

  & .kds pre > code {
    padding: 0;
    border: none;
    border-radius: 0;
    background: transparent;
    font: inherit;
    white-space: pre;
    color: var(--color-code-highlight-text);
  }

  ${codeHighlightTokensCss}
`;

const disclosureSummary = css`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  list-style: none;
  cursor: pointer;

  &::-webkit-details-marker {
    display: none;
  }
`;

export const ThinkingDetails = styled.details`
  min-width: 0;
  font-size: 12px;
  color: var(--color-font-placeholder);

  & > summary {
    ${disclosureSummary};
    width: fit-content;
    font-style: italic;
  }

  &[open] > summary > .ai-chevron {
    transform: translateY(-2px) rotate(45deg);
  }
`;

export const ThinkingBody = styled.pre`
  margin: 6px 0 0;
  padding: 6px 10px;
  max-height: 200px;
  overflow: auto;
  border-left: 2px solid var(--color-border-medium-light);
  font-family: inherit;
  font-size: 12px;
  line-height: 1.5;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

export const ToolCard = styled.details`
  min-width: 0;
  border: 1px solid var(--color-border-medium-light);
  border-radius: 4px;
  background: var(--color-background-white);
  font-size: 12px;

  & > summary {
    ${disclosureSummary};
    padding: 5px 8px;
  }

  & > summary:hover {
    background: var(--color-background-hover);
  }

  &[open] > summary > .ai-chevron {
    transform: translateY(-2px) rotate(45deg);
  }
`;

export const ToolGroupBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 6px;
  border-top: 1px solid var(--color-border-medium-light);
`;

export const ToolRowStatic = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  padding: 5px 8px;
  border: 1px solid var(--color-border-medium-light);
  border-radius: 4px;
  font-size: 12px;
`;

const toolLabelText = css`
  flex-shrink: 0;
  font-family: ${MONO_FONT};
  font-weight: 600;
  color: var(--color-font-primary);
`;

export const ToolGroupLabel = styled.span`
  ${toolLabelText};
`;

export const ToolName = styled.span`
  ${toolLabelText};
`;

export const ToolArgs = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: ${MONO_FONT};
  color: var(--color-font-secondary);
`;

export const ToolPreview = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--color-font-placeholder);
`;

/** A tool's own display title: readable text, not the muted preview. */
export const ToolTitle = styled(ToolPreview)`
  color: var(--color-font-secondary);
`;

export const RowChevron = styled.span`
  flex-shrink: 0;
  width: 6px;
  height: 6px;
  margin: 0 3px 0 auto;
  border-right: 1.5px solid var(--color-font-placeholder);
  border-bottom: 1.5px solid var(--color-font-placeholder);
  transform: translateX(-1px) rotate(-45deg);
`;

export const StatusIcon = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 14px;
`;

export const ToolDetail = styled.pre`
  margin: 0;
  padding: 8px 10px;
  max-height: 200px;
  overflow: auto;
  border-top: 1px solid var(--color-border-medium-light);
  font-family: ${MONO_FONT};
  font-size: 11px;
  line-height: 16px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  color: var(--color-code-highlight-text);
  background: var(--color-code-highlight-surface);
`;

export const EventRow = styled.div<{ $tone: 'success' | 'info' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 0;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  line-height: 16px;
  color: ${({ $tone }) =>
    $tone === 'success'
      ? 'var(--color-feedback-success)'
      : 'var(--color-font-secondary)'};
  background: ${({ $tone }) =>
    $tone === 'success'
      ? 'var(--color-feedback-success-bg)'
      : 'var(--color-background-lightest)'};
`;

export const NoticeRow = styled.div<{ $error: boolean }>`
  min-width: 0;
  padding: ${({ $error }) => ($error ? '6px 10px' : '0')};
  border-radius: 4px;
  font-size: 12px;
  line-height: 16px;
  overflow-wrap: anywhere;
  color: ${({ $error }) =>
    $error ? 'var(--color-feedback-error)' : 'var(--color-font-placeholder)'};
  background: ${({ $error }) =>
    $error ? 'var(--color-feedback-error-bg)' : 'transparent'};
  text-align: ${({ $error }) => ($error ? 'left' : 'center')};
`;

export const NoticeDetails = styled.ul`
  margin: 4px 0 0;
  padding-left: 16px;
  text-align: left;
`;

export const NoticeCode = styled.span`
  display: block;
  margin-top: 2px;
  font-family: ${MONO_FONT};
  font-size: 11px;
  color: var(--color-font-placeholder);
`;

export const ToolMeta = styled.span`
  flex-shrink: 0;
  font-size: 11px;
  color: var(--color-font-placeholder);
  white-space: nowrap;
`;

export const ActivityLabel = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const WorkingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  font-size: 12px;
  color: var(--color-font-placeholder);
`;

export const ComposerRoot = styled.div`
  flex-shrink: 0;
  min-width: 0;
`;

export const ComposerStatus = styled.span<{ $tone: 'error' | 'muted' }>`
  flex-shrink: 0;
  font-size: 12px;
  line-height: 16px;
  white-space: nowrap;
  color: ${({ $tone }) =>
    $tone === 'error'
      ? 'var(--color-feedback-error)'
      : 'var(--color-font-placeholder)'};
`;

export const ComposerSend = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  height: 25px;
  flex-shrink: 0;
  padding: 0 15px 5px;

  & > :empty {
    margin-right: -12px;
  }
`;

export const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;
